import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { ResponsiveContainer, ComposedChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { Receipt, Coffee } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, ChartTooltip, PageHeader, QueryState, CALUM, ErrorBox } from '../components/charts';
import DateRangeControl from '../components/DateRangeControl';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatCurrency, formatNumber, formatCurrency2, formatMonth } from '../lib/format';
import { todayISO } from '../lib/day';

const n2 = (v) => (v == null ? '–' : String(v).replace('.', ','));

// Backend payment-method keys (lower-cased) -> readable Dutch label (t() localises).
const PAY_LABEL = {
  cash: 'Cash', bancontact: 'Bancontact', card: 'Kaart', visa: 'Visa', mastercard: 'Mastercard',
  maestro: 'Maestro', payconiq: 'Payconiq', voucher: 'Bon/voucher', unknown: 'Onbekend',
};

export default function Receipts() {
  const t = useT();
  const [range, setRange] = useState({ preset: 'today', start_date: todayISO(), end_date: todayISO() });   // open on the current day
  const p = { start_date: range.start_date, end_date: range.end_date };

  const rc = useApi(() => api.receiptStats(p), [range.start_date, range.end_date]);
  const df = useApi(() => api.drinksWithFood(p), [range.start_date, range.end_date]);
  const pm = useApi(() => api.paymentMethods(p), [range.start_date, range.end_date]);
  const rd = useApi(() => api.receiptDistribution(p), [range.start_date, range.end_date]);
  const bp = useApi(() => api.basketProfile(p), [range.start_date, range.end_date]);
  const bc = useApi(() => api.basketComposition(p), [range.start_date, range.end_date]);

  // basket composition over time (months with too few tickets are noise)
  const comp = (bc.data?.monthly || []).filter((m) => m.tickets > 50).map((m) => ({ ...m, label: formatMonth(m.month) }));
  const h = bc.data?.headline || {};
  const d = df.data;
  const combos = d ? [
    { label: t('Drank + eten'), v: d.drink_and_food, c: CALUM.gold },
    { label: t('Enkel drank'), v: d.drink_only, c: CALUM.green },
    { label: t('Enkel eten'), v: d.food_only, c: CALUM.mint },
    { label: t('Anders'), v: d.neither, c: '#cbd5e1' },
  ] : [];
  const comboMax = Math.max(1, ...combos.map((x) => x.v));
  const payLabel = (m) => t(PAY_LABEL[m] || m);

  return (
    <PageShell page="Kassabonnen" accent="green">
      <PageHeader title={t("Kassabonnen")} subtitle={t("Hoe ziet een bon eruit — waarde, samenstelling, drank mét eten en betaalwijze.")}>
        <DateRangeControl value={range} onChange={setRange} dataRange={rc.data?.window && { start: rc.data.window.start, end: rc.data.window.end }} />
      </PageHeader>

      {rc.error && <ErrorBox error={rc.error} onRetry={rc.reload} />}

      {rc.data && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <KpiTile label={t("Gemiddeld per bon")} value={formatCurrency2(rc.data.avg_per_receipt)} sub={t("afgelopen periode")} accent="gold" icon={Receipt} />
          <KpiTile label={t("Bonnen per dag")} value={formatNumber(rc.data.avg_receipts_per_day)} sub={`${rc.data.open_days} ${t("open dagen")}`} accent="green" icon={Receipt} />
          <KpiTile label={t("Mediaan per bon")} value={rd.data?.median != null ? formatCurrency2(rd.data.median) : '–'} sub={t("de helft van de bonnen zit eronder")} accent="mint" icon={Receipt} />
          <KpiTile label={t("Drank mét eten")} value={d ? `${d.pct_drinks_with_food}%` : '–'}
            sub={d ? `${d.pct_drinks_alone}% ${t("enkel drank")}` : (df.loading ? t('Laden…') : '')} accent="gold" icon={Coffee} />
        </div>
      )}

      <SectionCard title={t("Samenstelling per bon — evolutie")}
        subtitle={t("Artikelen (hoeveelheden) en verschillende producten zijn betrouwbaar; het aantal bonregels springt bij de overstap naar de nieuwe kassa (mrt 2026) door een andere manier van registreren, niet door ander koopgedrag")}>
        <QueryState q={bc} isEmpty={(x) => !(x.monthly || []).some((m) => m.tickets > 50)}>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={comp} margin={{ left: 4, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={18} />
              <YAxis tick={{ fontSize: 11 }} domain={[0, 'auto']} />
              <Tooltip content={<ChartTooltip />} />
              <Bar dataKey="items_per_ticket" name={t("Artikelen/bon")} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
              <Line dataKey="distinct_per_ticket" name={t("Versch. producten/bon")} stroke={CALUM.green} strokeWidth={2} dot={false} />
              <Line dataKey="lines_per_ticket" name={t("Bonregels/bon")} stroke={CALUM.slate} strokeWidth={2} strokeDasharray="4 3" dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
          {h.items_per_ticket && (
            <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
              {t("Artikelen per bon:")} <b>{n2(h.items_per_ticket.first)}</b> → <b>{n2(h.items_per_ticket.last)}</b> ({h.items_per_ticket.chg_pct >= 0 ? '+' : ''}{n2(h.items_per_ticket.chg_pct)}%).
              {' '}{t("Dat artikelen > producten bevestigt dat hoeveelheden correct worden meegeteld (2× cappuccino = 2 artikelen, 1 product).")}
            </p>
          )}
        </QueryState>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title={t("Spreiding van de bonwaarde")} subtitle={rd.data?.median != null ? `${t("mediaan")} ${formatCurrency2(rd.data.median)} · ${t("gemiddelde")} ${formatCurrency2(rd.data.mean)} · ${String(rd.data.pct_le_5).replace('.', ',')}% ${t("van de bonnen is ≤ €5")}` : ''}>
          <QueryState q={rd} isEmpty={(x) => !x.histogram?.length}>
            {(x) => (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={x.histogram} margin={{ left: 0, right: 8, top: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="count" name={t("Bonnen")} fill={CALUM.gold} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">
                  {t("Het gemiddelde ligt boven de mediaan: veel kleine koffiebonnen, opgetild door grotere (groeps)lunches.")}
                  {x.pct_gt_25 != null && <> <b>{String(x.pct_gt_25).replace('.', ',')}%</b> {t("van de bonnen is groter dan €25.")}</>}
                </p>
              </>
            )}
          </QueryState>
        </SectionCard>

        <SectionCard title={t("De gemiddelde bestelling")} subtitle={bp.data?.avg_items != null ? `${String(bp.data.avg_items).replace('.', ',')} ${t("artikelen")} · ${String(bp.data.avg_products).replace('.', ',')} ${t("producten")} · ${formatCurrency2(bp.data.avg_value)} ${t("per bon")}` : ''}>
          <QueryState q={bp} isEmpty={(x) => !x.contains?.length && !x.favorites?.length}>
            {(x) => (
              <div className="grid grid-cols-2 gap-4 pt-1">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{t("Wat zit er in een bon?")}</div>
                  {(x.contains || []).map((c) => (
                    <div key={c.label} className="flex justify-between gap-2 text-xs py-1 border-t border-slate-100">
                      <span className="text-slate-600">{t(c.label)}</span>
                      <span className="font-semibold tabular-nums">{String(c.pct).replace('.', ',')}%</span>
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400 mb-1.5">{t("Favoriete producten")}</div>
                  {(x.favorites || []).slice(0, 6).map((f, i) => (
                    <div key={f.name} className="flex justify-between gap-2 text-xs py-1 border-t border-slate-100">
                      <span className="text-slate-600 truncate" title={f.name}>{i + 1}. {f.name}</span>
                      <span className="font-semibold tabular-nums shrink-0">{formatNumber(f.units)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </QueryState>
        </SectionCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <SectionCard title={t("Worden drankjes met eten gecombineerd?")} subtitle={d ? `${formatNumber(d.receipts_with_drink)} ${t("bonnen met een drankje")}` : ''}>
          <QueryState q={df} isEmpty={(x) => !x.total_receipts}>
            <div className="space-y-3 pt-2">
              {combos.map((x) => (
                <div key={x.label}>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>{x.label}</span>
                    <span className="font-semibold tabular-nums">{formatNumber(x.v)} ({Math.round(100 * x.v / (d?.total_receipts || 1))}%)</span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${100 * x.v / comboMax}%`, background: x.c }} />
                  </div>
                </div>
              ))}
              {d && (
                <p className="text-xs text-slate-500 pt-2 leading-relaxed">
                  {t("Van alle bonnen met een drankje wordt")} <b>{d.pct_drinks_with_food}%</b> {t("samen met eten besteld, en")} <b>{d.pct_drinks_alone}%</b> {t("apart.")}
                </p>
              )}
            </div>
          </QueryState>
        </SectionCard>

        <SectionCard title={t("Betaalmethodes")} subtitle={t("Aandeel van de omzet")} >
          <QueryState q={pm} isEmpty={(x) => !x.methods?.length} empty={t('Geen betaalgegevens voor deze periode.')}>
            {(x) => (
              <div className="space-y-3 pt-2">
                {x.methods.map((m) => (
                  <div key={m.method}>
                    <div className="flex justify-between text-xs text-slate-600 mb-1">
                      <span>{payLabel(m.method)}</span>
                      <span className="font-semibold tabular-nums">{formatCurrency(m.amount)} · {m.pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${m.pct}%`, background: CALUM.green }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </QueryState>
        </SectionCard>
      </div>
    </PageShell>
  );
}
